import type {
  FetchRefundCodeListParams,
  RefundCode
} from 'Services/ControlDataManagement/Features/RefundCode/types'
import type { ListResponse } from 'Services/ControlDataManagement/types'
import { createBaseApi } from 'Util/ApiClient'
import { RTKCacheTags } from 'Util/constants'

enum TagTypes {
  REFUND_CODE = 'RefundCode'
}

export const refundCodeApi = createBaseApi({
  reducerPath: 'refundCodeApi',
  keepUnusedDataFor: 10,
  tagTypes: [TagTypes.REFUND_CODE]
}).injectEndpoints({
  endpoints: builder => ({
    // biome-ignore lint/suspicious/noConfusingVoidType: RTK query requires void type for no params
    getRefundCodes: builder.query<ListResponse<RefundCode>, FetchRefundCodeListParams | void>({
      query: params => ({
        url: '/api/v1/refund-code/',
        params: params ?? undefined
      }),
      providesTags: result =>
        result
          ? [
              ...result.resources.map(
                ({ refund_code_id }) =>
                  ({ type: TagTypes.REFUND_CODE, id: refund_code_id }) as const
              ),
              { type: TagTypes.REFUND_CODE, id: RTKCacheTags.LIST }
            ]
          : [{ type: TagTypes.REFUND_CODE, id: RTKCacheTags.LIST }]
    }),
    getSingleRefundCode: builder.query<RefundCode, string>({
      query: refund_code_id => `/api/v1/refund-code/${refund_code_id}/`,
      providesTags: (_result, _error, refund_code_id) => [
        { type: TagTypes.REFUND_CODE, id: refund_code_id }
      ]
    }),
    createSingleRefundCode: builder.mutation<RefundCode, Partial<RefundCode>>({
      query: data => ({
        url: `/api/v1/refund-code/`,
        method: 'POST',
        body: data
      }),
      invalidatesTags: [{ type: TagTypes.REFUND_CODE, id: RTKCacheTags.LIST }]
    }),
    updateSingleRefundCode: builder.mutation<
      RefundCode,
      { refund_code_id: string } & Partial<RefundCode>
    >({
      query: ({ refund_code_id, ...data }) => ({
        url: `/api/v1/refund-code/${refund_code_id}/`,
        method: 'PUT',
        body: data
      }),
      invalidatesTags: (_result, _error, { refund_code_id }) => [
        { type: TagTypes.REFUND_CODE, id: refund_code_id }
      ]
    }),
    deleteSingleRefundCode: builder.mutation<Record<string, never>, string>({
      query: id => ({
        url: `/api/v1/refund-code/${id}/`,
        method: 'DELETE'
      }),
      invalidatesTags: [{ type: TagTypes.REFUND_CODE, id: RTKCacheTags.LIST }]
    })
  })
})

export const {
  useGetRefundCodesQuery,
  useGetSingleRefundCodeQuery,
  useCreateSingleRefundCodeMutation,
  useUpdateSingleRefundCodeMutation,
  useDeleteSingleRefundCodeMutation
} = refundCodeApi
